/**
 * Dziennik audytu (spec 01 §14).
 *
 * Wyszukiwarka + pięć filtrów, których wartości przychodzą z realnych zdarzeń
 * tenanta. Każda karta ma toggle „Pokaż dowód integralności" — dowód liczy
 * serwer z danych w bazie, więc czarny panel pokazuje wynik przeliczenia,
 * a nie napis „zweryfikowano" wpisany na sztywno w makiecie.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AUDIT_MODULE_KEYS, PERMISSION_ACTION_KEYS } from '@k1p4/shared'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import {
  Avatar,
  Button,
  Card,
  EmptyState,
  Input,
  Search,
  Select,
  Shield,
  Spinner,
  StatusPill,
  cx,
} from '@k1p4/ui'
import { formatDateTime } from '@k1p4/i18n'
import { shiftDay, warsawDay } from '@k1p4/shared'
import { api, apiToday, type AuditItem } from '../../api/client'
import styles from './Audit.module.css'

type DatePreset = 'any' | 'today' | 'week' | 'month'

const DATE_PRESETS: readonly DatePreset[] = ['any', 'today', 'week', 'month']
const PRESET_DAYS: Record<Exclude<DatePreset, 'any'>, number> = { today: 0, week: 6, month: 29 }

interface Filters {
  q: string
  actor: string
  objectType: string
  action: string
  module: string
  date: DatePreset
}

const EMPTY_FILTERS: Filters = { q: '', actor: '', objectType: '', action: '', module: '', date: 'any' }

/**
 * Początek zakresu dla presetu — doba w Europe/Warsaw, tak jak liczy ją serwer
 * (grupa 55): „Dziś" w przeglądarce za granicą znaczy dziś w Polsce.
 */
function fromDate(preset: DatePreset): string | undefined {
  if (preset === 'any') return undefined
  return shiftDay(warsawDay(apiToday()), -PRESET_DAYS[preset])
}

/**
 * Aktorem bywa adres e-mail (`kadry@firma.pl`) — awatar potrzebuje z niego słów,
 * a nie jednej litery, więc rozbijamy adres na człony.
 */
function actorName(label: string): string {
  return label.includes('@') ? label.split('@')[0]!.split(/[._-]+/u).join(' ') : label
}

/*
 * Katalog dziennika tłumaczony przy wyświetlaniu (fala 9, grupa 51). Kody
 * w bazie zostają — są częścią skrótu wiersza (ADR-0003) i wartością filtra;
 * kod spoza katalogu (wpis historyczny) pokazuje się jak jest.
 */
type AuditTranslate = ReturnType<typeof useTranslation>['t']

function auditActionLabel(t: AuditTranslate, code: string): string {
  return t(`audit.actions.${code.replaceAll('.', '__')}`, { defaultValue: code })
}

function auditModuleLabel(t: AuditTranslate, module: string): string {
  const key = AUDIT_MODULE_KEYS[module]
  return key ? t(`audit.modules.${key}`, { defaultValue: module }) : module
}

function auditPermissionLabel(t: AuditTranslate, permission: string): string {
  const [module, action] = permission.split(' : ')
  const actionKey = action ? PERMISSION_ACTION_KEYS[action] : undefined
  if (!module || !action || !actionKey) return permission
  return `${auditModuleLabel(t, module)} : ${t(`audit.permissionActions.${actionKey}`, { defaultValue: action })}`
}

export function Audit() {
  const { t } = useTranslation()
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS)
  const [openProof, setOpenProof] = useState<number | null>(null)

  const from = fromDate(filters.date)
  const journal = useQuery({
    queryKey: ['audit', filters.q, filters.actor, filters.objectType, filters.action, filters.module, from],
    queryFn: () =>
      api.audit({
        ...(filters.q ? { q: filters.q } : {}),
        ...(filters.actor ? { actor: filters.actor } : {}),
        ...(filters.objectType ? { objectType: filters.objectType } : {}),
        ...(filters.action ? { action: filters.action } : {}),
        ...(filters.module ? { module: filters.module } : {}),
        ...(from ? { from } : {}),
      }),
    /*
     * Każda zmiana filtra to nowy queryKey. Bez utrzymania poprzednich danych
     * `isPending` odmontowywałoby cały widok — razem z polem wyszukiwania —
     * po KAŻDYM wpisanym znaku, a użytkownik traciłby fokus po jednej literze.
     */
    placeholderData: keepPreviousData,
  })

  if (journal.isPending) return <Spinner label={t('common.loading')} />
  if (journal.isError) {
    return <EmptyState title={t('common.error')} description={t('common.retry')} />
  }

  const { items, filters: values, totals } = journal.data
  /* Serwer sprzed Fali 4 nie niesie journalTotal — wtedy liczy się wynik bez filtrów. */
  const journalTotal = journal.data.journalTotal ?? totals.all
  const filtered =
    filters.q !== '' ||
    filters.actor !== '' ||
    filters.objectType !== '' ||
    filters.action !== '' ||
    filters.module !== '' ||
    filters.date !== 'any'

  return (
    <>
      <p className={styles.lead}>{t('audit.lead')}</p>

      <div className={styles.bar}>
        <span className={styles.search}>
          <Search size={16} />
          <Input
            type="search"
            aria-label={t('audit.searchPlaceholder')}
            placeholder={t('audit.searchPlaceholder')}
            value={filters.q}
            onChange={(event) => setFilters({ ...filters, q: event.target.value })}
          />
        </span>

        <Select
          aria-label={t('audit.filters.actor')}
          value={filters.actor}
          onChange={(event) => setFilters({ ...filters, actor: event.target.value })}
        >
          <option value="">{t('audit.filters.actor')}</option>
          {values.actors.map((actor) => (
            <option key={actor} value={actor}>
              {actor}
            </option>
          ))}
        </Select>

        <Select
          aria-label={t('audit.filters.objectType')}
          value={filters.objectType}
          onChange={(event) => setFilters({ ...filters, objectType: event.target.value })}
        >
          <option value="">{t('audit.filters.objectType')}</option>
          {values.objectTypes.map((type) => (
            <option key={type} value={type}>
              {t(`audit.objects.${type}`, { defaultValue: type })}
            </option>
          ))}
        </Select>

        <Select
          aria-label={t('audit.filters.action')}
          value={filters.action}
          onChange={(event) => setFilters({ ...filters, action: event.target.value })}
        >
          <option value="">{t('audit.filters.action')}</option>
          {values.actions.map((action) => (
            <option key={action} value={action}>
              {auditActionLabel(t, action)}
            </option>
          ))}
        </Select>

        <Select
          aria-label={t('audit.filters.module')}
          value={filters.module}
          onChange={(event) => setFilters({ ...filters, module: event.target.value })}
        >
          <option value="">{t('audit.filters.module')}</option>
          {values.modules.map((module) => (
            <option key={module} value={module}>
              {auditModuleLabel(t, module)}
            </option>
          ))}
        </Select>

        <Select
          aria-label={t('audit.filters.date')}
          value={filters.date}
          onChange={(event) => setFilters({ ...filters, date: event.target.value as DatePreset })}
        >
          {DATE_PRESETS.map((preset) => (
            <option key={preset} value={preset}>
              {t(`audit.dates.${preset}`)}
            </option>
          ))}
        </Select>
      </div>

      <div className={styles.summary}>
        {/* Liczebnik z odmianą: licznik mówi o wyniku filtra, więc „2 zdarzenia" to codzienność (zgłoszenie 33). */}
        <span>
          {t('audit.countsAll', { count: totals.all })} · {t('audit.countsDenied', { count: totals.denied })}
        </span>
        {filtered ? (
          <Button size="sm" variant="secondary" onClick={() => setFilters(EMPTY_FILTERS)}>
            {t('audit.filters.reset')}
          </Button>
        ) : null}
      </div>

      {items.length === 0 ? (
        <EmptyState
          icon={<Shield size={28} />}
          /* Pusty dziennik to co innego niż pusty wynik filtra (grupa 144). */
          title={t(journalTotal === 0 ? 'audit.emptyAll' : 'audit.empty')}
          description={t(journalTotal === 0 ? 'audit.emptyAllBody' : 'audit.emptyBody')}
        />
      ) : (
        <div className={styles.events}>
          {items.map((item) => (
            <AuditCard
              key={item.id}
              item={item}
              open={openProof === item.id}
              onToggle={() => setOpenProof(openProof === item.id ? null : item.id)}
            />
          ))}
        </div>
      )}
    </>
  )
}

interface AuditCardProps {
  item: AuditItem
  open: boolean
  onToggle: () => void
}

function AuditCard({ item, open, onToggle }: AuditCardProps) {
  const { t } = useTranslation()

  /* Dowód liczy się dopiero po rozwinięciu — nikt nie potrzebuje 25 hashy naraz. */
  const proof = useQuery({
    queryKey: ['audit', 'proof', item.id],
    queryFn: () => api.auditProof(item.id),
    enabled: open,
  })

  return (
    <Card pad="md" className={styles.event}>
      <div className={styles.eventHead}>
        <Avatar name={actorName(item.actorLabel)} size={36} />
        <div className={styles.eventBody}>
          <p className={styles.eventTitle}>
            {/* „{kto} · zatwierdzenie listy płac” — bez rodzaju gramatycznego (D100). */}
            <strong>{item.actorLabel}</strong> · {auditActionLabel(t, item.action)}
          </p>
          <p className={styles.eventObject}>
            {t(`audit.objects.${item.objectType}`, { defaultValue: item.objectType })} ·{' '}
            {item.objectLabel}
          </p>
          <p className={styles.eventMeta}>
            <span>{formatDateTime(item.createdAt)}</span>
            <span>{auditPermissionLabel(t, item.permission)}</span>
          </p>
        </div>
        <StatusPill tone={item.result === 'ok' ? 'success' : 'danger'} dot>
          {t(`audit.result.${item.result}`)}
        </StatusPill>
      </div>

      <button type="button" className={styles.proofToggle} aria-expanded={open} onClick={onToggle}>
        {t(open ? 'audit.hideProof' : 'audit.showProof')}
      </button>

      {open ? (
        <div className={styles.proof}>
          <p className={styles.proofTitle}>{t('audit.proofTitle')}</p>
          {proof.isPending ? <p className={styles.proofNote}>{t('audit.proofLoading')}</p> : null}
          {proof.isError ? <p className={styles.proofBad}>{t('audit.proofError')}</p> : null}
          {proof.data ? (
            <>
              <p className={styles.proofRow}>
                <span className={styles.proofLabel}>{t('audit.proofHash')}</span>
                <code className={styles.hash}>{proof.data.hash}</code>
              </p>
              <p className={styles.proofRow}>
                <span className={styles.proofLabel}>{t('audit.proofPrev')}</span>
                <code className={cx(styles.hash, styles.hashPrev)}>{proof.data.prevHash}</code>
              </p>
              <p className={styles.proofNote}>
                {t('audit.proofChain', { seq: proof.data.seq, length: proof.data.chain.length })}
              </p>
              {proof.data.valid ? (
                <p className={styles.proofOk}>{t('audit.proofOk')}</p>
              ) : (
                <p className={styles.proofBad}>
                  {t(proof.data.hashMatches ? 'audit.proofUnlinked' : 'audit.proofBroken')}
                </p>
              )}
            </>
          ) : null}
        </div>
      ) : null}
    </Card>
  )
}
